import { useEffect, useState, useRef, ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';

interface RequireActiveBillingProps {
    children?: ReactNode;
}

export default function RequireActiveBilling({ children }: RequireActiveBillingProps) {
    const { session, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [checkingStudio, setCheckingStudio] = useState(false);
    const hasChecked = useRef(false);

    // SECURITY NOTE: This client-side validation is for UX only.
    // Server-side middleware on all protected API routes is the actual security enforcement.
    // Users can bypass this component, but they cannot bypass server-side validation.

    useEffect(() => {
        async function checkStudio() {
            // Don't check if auth is still loading or no session
            if (loading || !session?.access_token) {
                return;
            }

            // Don't re-check if we already checked successfully
            if (hasChecked.current) {
                return;
            }

            setCheckingStudio(true);
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
                    {
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (res.ok) {
                    const studio = await res.json();

                    // Check if user has completed onboarding and has active subscription
                    if (studio && studio.is_active) {
                        // User has active subscription - redirect to normal app
                        navigate('/app/dashboard');
                        return;
                    }

                    // Studio exists but is inactive - restrict to billing settings only
                    if (studio && !studio.is_active) {
                        // Allow access only to billing settings
                        const allowedPaths = ['/app/inactive-studio/settings/billing'];
                        if (!allowedPaths.includes(location.pathname)) {
                            navigate('/app/inactive-studio/settings/billing');
                            return;
                        }
                        hasChecked.current = true;
                        return;
                    }
                } else if (res.status === 404) {
                    // Studio not found - user needs to complete onboarding
                    navigate('/app/onboarding');
                    return;
                }

                // If we get here, something went wrong - redirect to onboarding for safety
                navigate('/app/onboarding');
            } catch (error) {
                // On error, redirect to onboarding to be safe
                navigate('/app/onboarding');
            } finally {
                setCheckingStudio(false);
            }
        }

        checkStudio();
    }, [session, loading, navigate, location]);

    // Additional effect to redirect if user tries to navigate elsewhere after initial check
    useEffect(() => {
        // Only run this effect if we've already done the initial check
        if (!hasChecked.current || loading || !session?.access_token) {
            return;
        }

        // If user is on the correct billing page, no need to redirect
        if (location.pathname === '/app/inactive-studio/settings/billing') {
            return;
        }

        // Check if current path is in the inactive studio flow but not billing
        if (location.pathname.startsWith('/app/inactive-studio/')) {
            // Already in inactive studio flow but not on billing - redirect to billing
            navigate('/app/inactive-studio/settings/billing', { replace: true });
            return;
        }

        // If they're trying to access any other part of the app, do a fresh studio check
        // This handles cases where they might try to manually navigate or use browser back/forward
        async function recheckAndRedirect() {
            if (!session?.access_token) {
                return;
            }
            
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
                    {
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (res.ok) {
                    const studio = await res.json();
                    
                    // If studio is still inactive, redirect to billing
                    if (studio && !studio.is_active) {
                        navigate('/app/inactive-studio/settings/billing', { replace: true });
                        return;
                    }
                    
                    // If studio is now active, allow normal navigation
                    if (studio && studio.is_active) {
                        // Studio reactivated, allow normal app access
                        return;
                    }
                }
            } catch (error) {
                // On error, redirect to billing to be safe
                navigate('/app/inactive-studio/settings/billing', { replace: true });
            }
        }

        recheckAndRedirect();
    }, [location.pathname, session, loading, navigate, hasChecked]);

    // Show loading only during the studio check, not during auth loading
    if (checkingStudio) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return children ? <>{children}</> : <Outlet />;
}
